import { db } from './firebase';
import { Preferences } from './WebPreferences';
import {
  doc, setDoc, getDoc, collection,
  query, orderBy, limit, getDocs,
  where, getCountFromServer,
} from 'firebase/firestore';

const COL_FOODS  = 'leaderboard_foods';
const COL_HEIGHT = 'leaderboard_foodheight';

// ── UUID helper ───────────────────────────────────────────────────────────────
function generateUUID() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

// ── Player profile ────────────────────────────────────────────────────────────
// Returns { uuid, name, countryCode } from local Preferences.
// uuid is auto-created on first call; name/countryCode may be null if not set yet.
export async function getOrCreatePlayer() {
  let [uuidRes, nameRes, countryRes] = await Promise.all([
    Preferences.get({ key: 'playerUUID' }),
    Preferences.get({ key: 'playerName' }),
    Preferences.get({ key: 'playerCountry' }),
  ]);

  let uuid = uuidRes.value;
  if (!uuid) {
    uuid = generateUUID();
    await Preferences.set({ key: 'playerUUID', value: uuid });
  }

  return {
    uuid,
    name:        nameRes.value    || null,
    countryCode: countryRes.value || null,
  };
}

export async function savePlayerProfile(name, countryCode) {
  await Promise.all([
    Preferences.set({ key: 'playerName',    value: name }),
    Preferences.set({ key: 'playerCountry', value: countryCode }),
  ]);

  // After saving profile, update existing leaderboard entries
  const [highFoodsRes, highHeightRes] = await Promise.all([
    Preferences.get({ key: 'highFoods' }),
    Preferences.get({ key: 'highHeight' }),
  ]);

  const highFoods  = parseInt(highFoodsRes.value || '0');
  const highHeight = parseInt(highHeightRes.value || '0');

  // Re-submit current best scores to update Firebase with real name
  // Use forceUpdate=true to ensure Firebase is updated even if score is the same
  if (highFoods > 0 || highHeight > 0) {
    await submitScore(highFoods, highHeight, true);
  }
}

// ── Country code → flag emoji ─────────────────────────────────────────────────
export function countryFlag(code) {
  if (!code || code === '-') return '-';  // Show hyphen for unset country
  if (code.length !== 2) return '🏳';
  return [...code.toUpperCase()].map(c =>
    String.fromCodePoint(0x1F1E6 + c.charCodeAt(0) - 65)
  ).join('');
}

// ── Default country for unregistered users ───────────────────────────────────
function detectCountry() {
  return '-'; // Hyphen indicates country not set yet
}

// ── Submit score (silent, called after game-over) ─────────────────────────────
// Only writes to Firestore if the new score beats the stored personal best.
// Set forceUpdate=true to update even if score is the same (used when updating name after registration)
export async function submitScore(foods, height, forceUpdate = false) {
  try {
    const player = await getOrCreatePlayer();

    // Generate placeholder values if not registered
    const displayName = player.name || `Player-${player.uuid.slice(-4)}`;
    const countryCode = player.countryCode || detectCountry();

    const now = new Date().toISOString();

    // Foods leaderboard
    const foodsRef  = doc(db, COL_FOODS, player.uuid);
    const foodsSnap = await getDoc(foodsRef);
    if (forceUpdate || !foodsSnap.exists() || foods > foodsSnap.data().score) {
      await setDoc(foodsRef, {
        name:        displayName,
        countryCode: countryCode,
        score:       foods,
        updatedAt:   now,
      });
    }

    // Height leaderboard
    const heightRef  = doc(db, COL_HEIGHT, player.uuid);
    const heightSnap = await getDoc(heightRef);
    if (forceUpdate || !heightSnap.exists() || height > heightSnap.data().score) {
      await setDoc(heightRef, {
        name:        displayName,
        countryCode: countryCode,
        score:       height,
        updatedAt:   now,
      });
    }
  } catch (e) {
    console.warn('[Leaderboard] submitScore failed:', e);
  }
}

// ── Fetch top N scores ────────────────────────────────────────────────────────
// type: 'foods' | 'height'
// Returns array of { rank, uuid, name, countryCode, score }
export async function getTopScores(type, limitCount = 100) {
  try {
    const col  = type === 'foods' ? COL_FOODS : COL_HEIGHT;
    const q    = query(collection(db, col), orderBy('score', 'desc'), limit(limitCount));
    const snap = await getDocs(q);
    return snap.docs.map((d, i) => ({
      rank:        i + 1,
      uuid:        d.id,
      name:        d.data().name,
      countryCode: d.data().countryCode,
      score:       d.data().score,
    }));
  } catch (e) {
    console.warn('[Leaderboard] getTopScores failed:', e);
    return [];
  }
}

// ── Get player's rank ─────────────────────────────────────────────────────────
// Returns 1-based rank, or null on error.
export async function getPlayerRank(type, uuid) {
  try {
    const col        = type === 'foods' ? COL_FOODS : COL_HEIGHT;
    const playerRef  = doc(db, col, uuid);
    const playerSnap = await getDoc(playerRef);
    if (!playerSnap.exists()) return null;

    const playerScore = playerSnap.data().score;
    const higherQuery = query(
      collection(db, col),
      where('score', '>', playerScore)
    );
    const countSnap = await getCountFromServer(higherQuery);
    return countSnap.data().count + 1;
  } catch (e) {
    console.warn('[Leaderboard] getPlayerRank failed:', e);
    return null;
  }
}
